import { z } from "zod";

import {
  boundedTextSchema,
  hasUniqueValues,
  kebabCaseIdentifierSchema,
  localOggAudioReferenceSchema,
  localResourceReferenceSchema
} from "@/domain/schemas";

export const MAX_TARGET_SEARCH_OBJECTS = 48;
export const MAX_TARGET_SEARCH_ROUNDS = 12;

const objectIdListSchema = z
  .array(kebabCaseIdentifierSchema)
  .min(1)
  .max(MAX_TARGET_SEARCH_OBJECTS)
  .refine((ids) => hasUniqueValues(ids), {
    message: "Object identifiers must be unique"
  });

export const targetSearchVisibleObjectSchema = z.strictObject({
  id: kebabCaseIdentifierSchema,
  label: boundedTextSchema(80),
  asset: localResourceReferenceSchema.optional()
});

const visibleObjectsSchema = z
  .array(targetSearchVisibleObjectSchema)
  .min(2)
  .max(MAX_TARGET_SEARCH_OBJECTS)
  .refine(
    (objects) => hasUniqueValues(objects.map(({ id }) => id)),
    { message: "Visible object identifiers must be unique" }
  );

export const targetSearchRoundSchema = z
  .strictObject({
    id: kebabCaseIdentifierSchema,
    prompt: boundedTextSchema(300),
    promptAudio: localOggAudioReferenceSchema.optional(),
    visibleObjects: visibleObjectsSchema,
    targetIds: objectIdListSchema,
    distractorIds: objectIdListSchema
  })
  .superRefine((round, context) => {
    const targetIds = new Set(round.targetIds);
    const visibleIds = new Set(round.visibleObjects.map(({ id }) => id));
    const referencedIds = new Set<string>();

    round.distractorIds.forEach((id, index) => {
      if (targetIds.has(id)) {
        context.addIssue({
          code: "custom",
          path: ["distractorIds", index],
          message: "An object cannot be both a target and a distractor"
        });
      }
    });

    (["targetIds", "distractorIds"] as const).forEach((field) => {
      round[field].forEach((id, index) => {
        referencedIds.add(id);
        if (!visibleIds.has(id)) {
          context.addIssue({
            code: "custom",
            path: [field, index],
            message: "Referenced object must exist in visibleObjects"
          });
        }
      });
    });

    round.visibleObjects.forEach(({ id }, index) => {
      if (!referencedIds.has(id)) {
        context.addIssue({
          code: "custom",
          path: ["visibleObjects", index, "id"],
          message: "Every visible object must be a target or distractor"
        });
      }
    });
  });

export const targetSearchPayloadSchema = z.strictObject({
  rounds: z
    .array(targetSearchRoundSchema)
    .min(1)
    .max(MAX_TARGET_SEARCH_ROUNDS)
    .refine((rounds) => hasUniqueValues(rounds.map(({ id }) => id)), {
      message: "Round identifiers must be unique"
    })
});

export type TargetSearchVisibleObject = z.infer<
  typeof targetSearchVisibleObjectSchema
>;
export type TargetSearchRoundDefinition = z.infer<
  typeof targetSearchRoundSchema
>;
export type TargetSearchPayload = z.infer<typeof targetSearchPayloadSchema>;

export interface ResolvedTargetSearchRound {
  readonly id: string;
  readonly prompt: string;
  readonly promptAudio?: string;
  readonly targetIds: readonly string[];
  readonly distractorIds: readonly string[];
  readonly visibleObjects: readonly TargetSearchVisibleObject[];
}

export function resolveTargetSearchRounds(
  payload: TargetSearchPayload
): ResolvedTargetSearchRound[] {
  return payload.rounds.map((round) => ({
    id: round.id,
    prompt: round.prompt,
    ...(round.promptAudio === undefined
      ? {}
      : { promptAudio: round.promptAudio }),
    targetIds: [...round.targetIds],
    distractorIds: [...round.distractorIds],
    visibleObjects: round.visibleObjects.map((object) => ({ ...object }))
  }));
}
